import React from 'react';
import { isLifeBot } from './lifeObjectType';
import { Checkbox, Skeleton, Menu, MenuItem } from '@mui/material';
import { renderableHtml } from './legacyHtml';
import { StudioIcon } from '../design/StudioShell';

const labels = {
  active: 'Published',
  draft: 'Draft',
  paused: 'Paused',
  expired: 'Ended',
  archived: 'Archived',
};
const signalNames = {
  qr: 'LifeCode',
  'geo-point': 'Location',
  'geo-fence': 'Location',
  geo: 'Location',
  spatial: 'Spatial',
  ble: 'Bluetooth',
  nfc: 'LifeTap',
  url: 'Web link',
  audio: 'Audio',
};
const icons = {
  qr: 'qr',
  'geo-point': 'place',
  'geo-fence': 'place',
  geo: 'place',
  spatial: 'spatial',
  ble: 'signals',
  nfc: 'signals',
  url: 'link',
  audio: 'audio',
};

export function cardSummary(item) {
  const text = (item.content || []).find(
    (entry) => entry.type === 'text'
  )?.body;
  if (text)
    return String(text)
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  const html = (item.content || []).find(
    (entry) => entry.type === 'html'
  )?.body;
  if (!html) return '';
  return new DOMParser()
    .parseFromString(renderableHtml(html), 'text/html')
    .body.textContent.replace(/\s+/g, ' ')
    .trim();
}

export function cardHtmlPreview(html) {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(
    renderableHtml(html),
    'text/html'
  );
  // Sanitized, unsupported embeds can leave a large empty layout wrapper.
  [...doc.querySelectorAll('div, section')].reverse().forEach((element) => {
    if (
      !element.textContent.trim() &&
      !element.querySelector('img,video,audio,iframe,svg')
    )
      element.remove();
  });
  return doc.body.innerHTML;
}

function HtmlThumbnail({ html, name }) {
  const container = React.useRef(null);
  const [width, setWidth] = React.useState(285);
  React.useEffect(() => {
    const element = container.current;
    const measure = () => {
      if (element.clientWidth) setWidth(element.clientWidth);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const scale = width / 480;
  return (
    <div className="lt-card-html-canvas" ref={container}>
      <iframe
        className="lt-card-html-preview"
        title={`${name} content thumbnail`}
        srcDoc={`<!doctype html><html><head><meta name="viewport" content="width=device-width, initial-scale=1"><style>html,body{margin:0;padding:0;overflow:hidden}body{overflow-wrap:break-word}img,video{max-width:100%;height:auto}*{box-sizing:border-box}</style></head><body>${html}</body></html>`}
        style={{
          width: 480,
          height: Math.ceil(400 / scale),
          transform: `scale(${scale})`,
        }}
        sandbox=""
        loading="lazy"
        tabIndex={-1}
        scrolling="no"
        aria-hidden="true"
      />
    </div>
  );
}

function MomentCard({
  item,
  onSelect,
  onEdit,
  onDelete,
  selectedIds,
  onToggleBulk,
  selectionMode,
}) {
  const content = item.content || [];
  const media = content.find(
    (entry) => entry.type === 'image' && (entry.url || entry.assetPreviewUrl)
  );
  const [failed, setFailed] = React.useState(false);
  const [actionAnchor, setActionAnchor] = React.useState(null);
  const html = content.find(
    (entry) => entry.type === 'html' && entry.body
  )?.body;
  const previewHtml = React.useMemo(() => cardHtmlPreview(html), [html]);
  const url = media?.assetPreviewUrl || media?.url;
  React.useEffect(() => setFailed(false), [url]);
  const summary = cardSummary(item);
  const primary =
    content.find((entry) => entry.type !== 'text')?.type || 'text';
  const types = [...new Set(content.map((entry) => entry.type))];
  const typeLabel =
    types.length > 1
      ? 'Mixed media'
      : {
          html: 'Web experience',
          document: 'Document',
          pdf: 'Document',
          image: 'Image',
          video: 'Video',
          audio: 'Audio',
          text: 'Written moment',
          link: 'Web link',
        }[primary] || primary;
  const anchors = item.anchors || [];
  const contexts = [
    ...new Set(anchors.map((a) => signalNames[a.type] || a.type)),
  ];
  const audience =
    item.audience?.visibility === 'public'
      ? 'Everyone'
      : item.audience?.identifiers?.length > 1
      ? 'Private group'
      : 'Private audience';
  const rawDate = item.metadata?.sourceCreatedAt || item.createdAt;
  const date = rawDate ? new Date(rawDate) : null;
  const dateLabel =
    date && !Number.isNaN(date.getTime())
      ? date.toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : '';
  return (
    <article className="lt-object-card">
      {selectionMode && (
        <Checkbox
          checked={selectedIds.includes(item.id)}
          onChange={() => onToggleBulk(item.id)}
          inputProps={{ 'aria-label': `Select ${item.name}` }}
          sx={{
            position: 'absolute',
            left: 7,
            top: 7,
            zIndex: 3,
            bgcolor: '#fff',
          }}
        />
      )}
      <div className="lt-card-toolbar">
        {!selectionMode && (
          <span className="lt-card-type">
            {typeLabel}
            {content.length > 1 ? ` · ${content.length}` : ''}
          </span>
        )}
        <button
          className="lt-card-edit"
          aria-label={`Actions for ${item.name}`}
          aria-haspopup="menu"
          aria-expanded={Boolean(actionAnchor)}
          onClick={(event) => setActionAnchor(event.currentTarget)}
        >
          Actions
        </button>
      </div>
      <Menu
        anchorEl={actionAnchor}
        open={Boolean(actionAnchor)}
        onClose={() => setActionAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            setActionAnchor(null);
            onEdit(item);
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setActionAnchor(null);
            onDelete(item);
          }}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
      </Menu>
      <button
        className="lt-card-open"
        aria-label={`Preview ${item.name}`}
        onClick={() => onSelect(item)}
      >
        <div className={`lt-card-art type-${primary}`}>
          {url && !failed ? (
            <img
              src={url}
              alt=""
              loading="lazy"
              onError={() => setFailed(true)}
            />
          ) : failed && summary ? (
            <p className="lt-art-quote">“{summary}”</p>
          ) : previewHtml && !failed ? (
            <HtmlThumbnail html={previewHtml} name={item.name} />
          ) : summary ? (
            <p className="lt-art-quote">“{summary}”</p>
          ) : (
            <span className="lt-art-glyph">
              <StudioIcon
                size={33}
                name={
                  primary === 'html' || primary === 'link'
                    ? 'link'
                    : primary === 'audio'
                    ? 'audio'
                    : primary === 'text'
                    ? 'text'
                    : 'media'
                }
              />
            </span>
          )}
        </div>
        <div className="lt-card-body">
          <div className="lt-card-context">
            <StudioIcon name={icons[anchors[0]?.type] || 'people'} size={12} />
            <span>{contexts.length ? contexts.join(' + ') : audience}</span>
          </div>
          <h3>{item.name}</h3>
          {isLifeBot(item) && (
            <span className="lt-status">LifeBot · Interactive</span>
          )}
          <p className="lt-card-summary">
            {summary && summary !== item.name
              ? summary
              : `${audience} · ${typeLabel}${
                  anchors.length ? ' · All discovery conditions apply' : ''
                }`}
          </p>
          <div className="lt-card-footer">
            <span className={`lt-status ${item.lifecycle?.status}`}>
              {labels[item.lifecycle?.status] ||
                item.lifecycle?.status ||
                'Draft'}
            </span>
            <span>{dateLabel}</span>
          </div>
        </div>
      </button>
      {failed && (
        <button className="lt-media-reconnect" onClick={() => onEdit(item)}>
          Image unavailable · Reconnect media ↗
        </button>
      )}
    </article>
  );
}

export default function EditorialCollectionStream({
  items,
  loading,
  selectedIds,
  onSelect,
  onToggleBulk,
  onEdit,
  onDelete,
  selectionMode = false,
  view = 'grid',
}) {
  if (loading && !items.length)
    return <Skeleton variant="rounded" height={500} />;
  return (
    <div className={`lt-collection-grid ${view === 'list' ? 'is-list' : ''}`}>
      {items.map((item) => (
        <MomentCard
          key={item.id}
          {...{
            item,
            onSelect,
            onEdit,
            onDelete,
            selectedIds,
            onToggleBulk,
            selectionMode,
          }}
        />
      ))}
    </div>
  );
}
